import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { doc, onSnapshot, setDoc } from "firebase/firestore";
import MediaRenderer from "../../components/MediaRenderer";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { db } from "../../firebase";
import { sanitizeForFirestore } from "../../lib/firebaseSafe";
import { uploadAdminMedia } from "../../lib/mediaUpload";
import { ADMIN_EMAILS } from "../../lib/platformData";
import { mergeSiteContent } from "../../utils/helpers";

function setIn(obj, path, value) {
  const keys = path.split(".");
  const clone = { ...obj };
  let cursor = clone;
  keys.forEach((key, index) => {
    if (index === keys.length - 1) {
      cursor[key] = value;
      return;
    }
    cursor[key] = Array.isArray(cursor[key]) ? [...cursor[key]] : { ...(cursor[key] || {}) };
    cursor = cursor[key];
  });
  return clone;
}

function Field({ label, value, onChange, textarea = false }) {
  const Input = textarea ? "textarea" : "input";
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-gray-400">{label}</span>
      <Input
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
        className={`w-full rounded border border-white/10 bg-black px-3 py-2 text-white outline-none focus:border-red-500 ${
          textarea ? "min-h-[110px]" : ""
        }`}
      />
    </label>
  );
}

export default function AdminStudio() {
  const { user, loading, loginWithGoogle, logout } = useAuth();
  const toast = useToast();
  const [content, setContent] = useState(() => mergeSiteContent());
  const [uploading, setUploading] = useState("");
  const isAdmin = Boolean(user && ADMIN_EMAILS.includes(user.email));

  useEffect(() => {
    if (!isAdmin) return undefined;
    return onSnapshot(doc(db, "admin", "content"), (snap) => {
      setContent(mergeSiteContent(snap.exists() ? snap.data() : {}));
    });
  }, [isAdmin]);

  const savePath = async (path, value, message = "Studio sauvegardé") => {
    const next = setIn(content, path, value);
    setContent(next);
    try {
      await setDoc(doc(db, "admin", "content"), sanitizeForFirestore(next), { merge: true });
      toast.success(message);
    } catch (err) {
      toast.error("Sauvegarde impossible", err.message);
    }
  };

  const uploadPath = async (file, path, label) => {
    if (!file) return;
    setUploading(label);
    try {
      const url = await uploadAdminMedia(file, "site/studio");
      await savePath(path, url, "Média studio mis à jour");
    } catch (err) {
      toast.error("Upload impossible", err.message);
    } finally {
      setUploading("");
    }
  };

  if (loading) return <main className="min-h-screen bg-black p-8 text-white">Chargement...</main>;

  if (!user || !isAdmin) {
    return (
      <main className="grid min-h-screen place-items-center bg-black px-6 text-white">
        <section className="max-w-md rounded-lg border border-white/10 bg-zinc-950 p-6 text-center">
          <h1 className="text-3xl font-black uppercase">Admin Studio</h1>
          <p className="mt-3 text-gray-400">Connexion admin requise.</p>
          {user && !isAdmin ? (
            <button onClick={logout} className="mt-6 rounded-full bg-red-600 px-5 py-3 text-sm font-bold uppercase">Changer de compte</button>
          ) : (
            <button onClick={loginWithGoogle} className="mt-6 rounded-full bg-white px-5 py-3 text-sm font-bold uppercase text-black">Connexion Google</button>
          )}
        </section>
      </main>
    );
  }

  const page = content.pages.studio;

  return (
    <main className="min-h-screen bg-black p-4 text-white md:p-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <header className="flex flex-col justify-between gap-4 rounded-lg border border-white/10 bg-zinc-950 p-5 md:flex-row md:items-center">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-red-500">Admin</p>
            <h1 className="mt-2 text-4xl font-black uppercase">Studio</h1>
            <p className="mt-2 text-sm text-gray-400">Réglages publics, réservation et médias du studio.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/studio" className="rounded-full bg-white px-4 py-2 text-sm font-bold uppercase text-black">Voir le studio</Link>
            <Link to="/reservation-studio" className="rounded-full border border-white/15 px-4 py-2 text-sm font-bold uppercase">Tester réservation</Link>
          </div>
        </header>

        <section className="rounded-lg border border-white/10 bg-zinc-950 p-5">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-red-500">Accès</p>
          <h2 className="mt-2 text-2xl font-black uppercase">Studio privé</h2>
          <div className="mt-5 flex flex-wrap gap-3">
            <label className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm font-bold uppercase">
              <input
                type="checkbox"
                checked={page.bookingEnabled !== false}
                onChange={(event) => savePath("pages.studio.bookingEnabled", event.target.checked, event.target.checked ? "Réservation studio activée" : "Réservation studio désactivée")}
              />
              Réservation active
            </label>
            <label className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm font-bold uppercase">
              <input
                type="checkbox"
                checked={page.paymentEnabled === true}
                onChange={(event) => savePath("pages.studio.paymentEnabled", event.target.checked, event.target.checked ? "Paiement studio activé" : "Paiement studio désactivé")}
              />
              Paiement studio actif
            </label>
          </div>
          <div className="mt-5">
            <Field
              textarea
              label="Message si réservation désactivée"
              value={page.privateMessage}
              onChange={(value) => savePath("pages.studio.privateMessage", value)}
            />
          </div>
        </section>

        <section className="rounded-lg border border-white/10 bg-zinc-950 p-5">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-red-500">Hero studio</p>
          <h2 className="mt-2 text-2xl font-black uppercase">Médias et textes</h2>
          <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-[280px_1fr]">
            <MediaRenderer
              src={page.heroVideo || page.heroImage}
              type={page.heroVideo ? "video" : "image"}
              fallback={page.heroImage}
              alt=""
              background
              className="aspect-[4/3] h-full w-full rounded object-cover"
            />
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field label="Eyebrow" value={page.eyebrow} onChange={(value) => savePath("pages.studio.eyebrow", value)} />
              <Field label="Titre" value={page.title} onChange={(value) => savePath("pages.studio.title", value)} />
              <div className="md:col-span-2">
                <Field textarea label="Description" value={page.description} onChange={(value) => savePath("pages.studio.description", value)} />
              </div>
              <Field label="URL vidéo hero" value={page.heroVideo} onChange={(value) => savePath("pages.studio.heroVideo", value)} />
              <Field label="URL image fallback" value={page.heroImage} onChange={(value) => savePath("pages.studio.heroImage", value)} />
            </div>
          </div>
          <div className="mt-5 flex flex-wrap gap-3">
            <label className={`cursor-pointer rounded-full bg-white px-4 py-2 text-xs font-bold uppercase text-black ${uploading ? "pointer-events-none opacity-60" : ""}`}>
              {uploading === "video" ? "Upload vidéo..." : "Remplacer la vidéo"}
              <input type="file" accept="video/*" className="hidden" onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = "";
                uploadPath(file, "pages.studio.heroVideo", "video");
              }} />
            </label>
            <label className={`cursor-pointer rounded-full border border-white/15 px-4 py-2 text-xs font-bold uppercase ${uploading ? "pointer-events-none opacity-60" : ""}`}>
              {uploading === "image" ? "Upload image..." : "Remplacer l'image fallback"}
              <input type="file" accept="image/*" className="hidden" onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = "";
                uploadPath(file, "pages.studio.heroImage", "image");
              }} />
            </label>
          </div>
        </section>
      </div>
    </main>
  );
}
